<script setup lang="ts">
const { t, tm, rt } = useI18n()

type Hub = { code: string; city: string; tag: string }
const hubs = computed(() =>
  (tm('hubs.list') as any[]).map((h) => ({
    code: rt(h.code),
    city: rt(h.city),
    tag:  rt(h.tag),
  })) as Hub[],
)

// Place each hub around the orbit circle. Indexes evenly distributed.
const positions = computed(() => {
  const n = hubs.value.length
  return hubs.value.map((_, i) => {
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2 // start at top
    const cx = 50 + Math.cos(angle) * 44
    const cy = 50 + Math.sin(angle) * 44
    return { left: `${cx}%`, top: `${cy}%` }
  })
})
</script>

<template>
  <section id="hubs" class="rod-section relative">
    <div class="rod-container">
      <div class="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
        <div data-reveal>
          <div class="rod-eyebrow mb-5">🌍 {{ t('hubs.eyebrow') }}</div>
          <h2 class="rod-h2 mb-5">{{ t('hubs.title') }}</h2>
          <p class="text-lg text-[var(--color-text-muted)] mb-10 leading-relaxed">{{ t('hubs.lead') }}</p>

          <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <!-- Highlight the first card to draw the eye into the list. -->
            <div v-for="(h, i) in hubs" :key="h.code"
                 class="rod-card !p-4 flex items-center gap-3"
                 :class="i === 0 ? 'rod-card--glow' : ''">
              <div class="font-mono font-bold text-base rod-gradient-text">{{ h.code }}</div>
              <div class="min-w-0">
                <div class="text-sm font-semibold truncate">{{ h.city }}</div>
                <div class="text-xs text-[var(--color-text-dim)] truncate">{{ h.tag }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Orbit globe -->
        <div class="rod-globe" data-reveal>
          <div class="rod-globe__ring" />
          <div class="rod-globe__ring rod-globe__ring--inner" />
          <div class="rod-globe__core" />
          <div
            v-for="(h, i) in hubs"
            :key="h.code"
            class="rod-hub-pin"
            :style="positions[i]"
          >
            <div class="rod-hub-pin__dot" />
            <span>{{ h.code }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
